/**
 * FHIR Supply Sync Panel
 * Visualizes SupplyRequest/SupplyDelivery EHR handshake status.
 */

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Shield,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface FhirSyncEntry {
  id: string;
  resourceType: string;
  direction: 'inbound' | 'outbound';
  status: 'success' | 'pending' | 'failed';
  description: string;
  timestamp: string;
  fhirResourceId?: string;
}

const DEMO_SYNC_LOG: FhirSyncEntry[] = [
  {
    id: 'fs-1',
    resourceType: 'SupplyRequest',
    direction: 'outbound',
    status: 'success',
    description: 'Push updated Carpal Tunnel preference card → EHR Materials Management',
    timestamp: '2026-02-10T16:42:01Z',
    fhirResourceId: 'SupplyRequest/sr-ctr-larkin-v3',
  },
  {
    id: 'fs-2',
    resourceType: 'SupplyDelivery',
    direction: 'inbound',
    status: 'success',
    description: 'Pull confirmed delivery for TKA case #2026-0209-A (implant set)',
    timestamp: '2026-02-09T15:10:00Z',
    fhirResourceId: 'SupplyDelivery/sd-tka-20260209a',
  },
  {
    id: 'fs-3',
    resourceType: 'SupplyRequest',
    direction: 'outbound',
    status: 'success',
    description: 'Push updated TKA preference card → remove Autograft Bone Chips',
    timestamp: '2026-02-09T14:15:01Z',
    fhirResourceId: 'SupplyRequest/sr-tka-larkin-v7',
  },
  {
    id: 'fs-4',
    resourceType: 'SupplyRequest',
    direction: 'outbound',
    status: 'success',
    description: 'Push updated THA preference card → adjust drain quantity 2→1',
    timestamp: '2026-02-06T11:30:01Z',
    fhirResourceId: 'SupplyRequest/sr-tha-larkin-v5',
  },
  {
    id: 'fs-5',
    resourceType: 'SupplyDelivery',
    direction: 'inbound',
    status: 'pending',
    description: 'Awaiting delivery confirmation for Shoulder Arthroscopy case #2026-0210-B',
    timestamp: '2026-02-10T08:00:00Z',
  },
  {
    id: 'fs-6',
    resourceType: 'SupplyRequest',
    direction: 'outbound',
    status: 'failed',
    description: 'Push Shoulder Arthroscopy card update failed — EHR timeout',
    timestamp: '2026-02-08T09:12:00Z',
    fhirResourceId: 'SupplyRequest/sr-sa-larkin-v2',
  },
];

function StatusIcon({ status }: { status: FhirSyncEntry['status'] }) {
  if (status === 'success') return <CheckCircle2 className="w-4 h-4 text-success" />;
  if (status === 'pending') return <Clock className="w-4 h-4 text-warning" />;
  return <AlertTriangle className="w-4 h-4 text-destructive" />;
}

export function FhirSupplySyncPanel() {
  return (
    <Card className="border-border">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-primary" />
          <div>
            <CardTitle className="text-base">FHIR Supply Sync Log</CardTitle>
            <CardDescription className="text-xs">
              Bi-directional SupplyRequest (push) and SupplyDelivery (pull) transactions with the hospital EHR.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <Separator />
      <CardContent className="pt-3 space-y-2">
        {DEMO_SYNC_LOG.map(entry => (
          <div
            key={entry.id}
            className={cn(
              'flex items-start gap-3 px-3 py-2.5 rounded-lg border',
              entry.status === 'failed' && 'border-destructive/20 bg-destructive/5',
              entry.status === 'pending' && 'border-warning/20 bg-warning/5',
              entry.status === 'success' && 'border-border bg-card',
            )}
          >
            {/* Direction icon */}
            <div className="mt-0.5 shrink-0">
              {entry.direction === 'outbound' ? (
                <ArrowUpFromLine className="w-4 h-4 text-primary" />
              ) : (
                <ArrowDownToLine className="w-4 h-4 text-chart-2" />
              )}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 space-y-1">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[10px] border-border">
                  {entry.resourceType}
                </Badge>
                <Badge variant="outline" className={cn(
                  'text-[10px]',
                  entry.direction === 'outbound' ? 'border-primary/30 text-primary' : 'border-chart-2/30 text-chart-2',
                )}>
                  {entry.direction === 'outbound' ? 'PUSH' : 'PULL'}
                </Badge>
              </div>
              <p className="text-sm">{entry.description}</p>
              <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                <span>{new Date(entry.timestamp).toLocaleString()}</span>
                {entry.fhirResourceId && (
                  <span className="font-mono">{entry.fhirResourceId}</span>
                )}
              </div>
            </div>

            {/* Status */}
            <StatusIcon status={entry.status} />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
